import { Button, Modal, Select } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ERP_OUTBOX_KINDS, type ErpOutboxRow, type ErpOutboxStatus } from '@warehouse/shared';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { usePermissionCheck } from '../../../auth/auth-store';
import { DataTable, type DataTableColumn } from '../../../components/DataTable';
import { PageHeader } from '../../../components/PageHeader';
import { useConfirm } from '../../../components/ConfirmDialog';
import { useListState } from '../../../hooks/useListState';
import { formatDateTime } from '../../../lib/format';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { cancelOutbox, erpKeys, fetchErpStatus, fetchOutbox, retryOutbox } from '../api';
import { EnvironmentBanner } from '../components/EnvironmentBanner';
import { OutboxStatusBadge, PayloadBlock } from '../components/status-badges';

const STATUSES: ErpOutboxStatus[] = ['pending', 'sent', 'failed', 'dead'];

/**
 * The outbox browser: everything the WMS has queued FOR Business Central, with
 * the two operator affordances that matter when BC has been down — retry (reset
 * the attempt budget and go now) and cancel (stop retrying, for good).
 */
export function OutboxPage(): ReactNode {
  const { t } = useTranslation(['bcIntegration', 'common']);
  const can = usePermissionCheck();
  const mayTrigger = can(['integration.sync.trigger']);
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const { state, setState, setFilter } = useListState({ defaultSort: 'createdAt', defaultDir: 'desc' });
  const [selected, setSelected] = useState<ErpOutboxRow | null>(null);

  const status = useQuery({ queryKey: erpKeys.status, queryFn: fetchErpStatus });
  const list = useQuery({ queryKey: erpKeys.outbox(state), queryFn: () => fetchOutbox(state) });
  useLiveQuery(['erp'], ['erp']);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['erp'] });
  };

  const retry = useMutation({
    mutationFn: retryOutbox,
    onSuccess: () => {
      toastSuccess(t('bcIntegration:toast.retryQueued'));
      invalidate();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const cancel = useMutation({
    mutationFn: (id: string) => cancelOutbox(id),
    onSuccess: () => {
      toastSuccess(t('bcIntegration:toast.cancelled'));
      invalidate();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const columns: DataTableColumn<ErpOutboxRow>[] = [
    {
      id: 'kind',
      header: t('bcIntegration:outbox.kind'),
      sortField: 'kind',
      cell: (row) => <span className="font-mono text-xs">{row.kind}</span>,
    },
    {
      id: 'status',
      header: t('bcIntegration:outbox.status'),
      sortField: 'status',
      cell: (row) => <OutboxStatusBadge status={row.status} />,
    },
    {
      id: 'attempts',
      header: t('bcIntegration:outbox.attempts'),
      sortField: 'attempts',
      align: 'right',
      cell: (row) => row.attempts,
    },
    {
      id: 'nextAt',
      header: t('bcIntegration:outbox.nextAt'),
      sortField: 'nextAt',
      hideBelow: 'md',
      cell: (row) => (row.status === 'pending' ? formatDateTime(row.nextAt) : '—'),
    },
    {
      id: 'createdAt',
      header: t('bcIntegration:outbox.created'),
      sortField: 'createdAt',
      hideBelow: 'lg',
      cell: (row) => formatDateTime(row.createdAt),
    },
    {
      id: 'lastError',
      header: t('bcIntegration:outbox.lastError'),
      cell: (row) => (
        <span className="block max-w-96 truncate text-xs text-red-600" title={row.lastError ?? undefined}>
          {row.lastError ?? '—'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (row) =>
        mayTrigger ? (
          <span className="flex justify-end gap-1.5">
            {row.status !== 'sent' ? (
              <Button
                variant="ghost"
                size="sm"
                disabled={retry.isPending}
                onClick={(event) => {
                  event.stopPropagation();
                  retry.mutate(row.id);
                }}
              >
                {t('bcIntegration:actions.retry')}
              </Button>
            ) : null}
            {row.status === 'pending' || row.status === 'failed' ? (
              <Button
                variant="ghost"
                size="sm"
                disabled={cancel.isPending}
                onClick={(event) => {
                  event.stopPropagation();
                  void (async () => {
                    const ok = await confirm({
                      title: t('bcIntegration:confirm.cancelTitle'),
                      message: t('bcIntegration:confirm.cancelBody'),
                      confirmLabel: t('bcIntegration:confirm.cancelConfirm'),
                    });
                    if (ok) cancel.mutate(row.id);
                  })();
                }}
              >
                {t('bcIntegration:actions.cancel')}
              </Button>
            ) : null}
          </span>
        ) : null,
    },
  ];

  return (
    <>
      {dialog}
      <PageHeader title={t('bcIntegration:outbox.title')} subtitle={t('bcIntegration:outbox.subtitle')} />
      <EnvironmentBanner status={status.data} />

      <DataTable
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        state={state}
        onStateChange={setState}
        total={list.data?.total ?? null}
        nextCursor={list.data?.nextCursor ?? null}
        loading={list.isLoading}
        error={list.error}
        searchPlaceholder={t('bcIntegration:outbox.search')}
        emptyTitle={t('bcIntegration:outbox.empty')}
        emptyDescription={t('bcIntegration:outbox.emptyDescription')}
        onRowClick={setSelected}
        filters={
          <>
            <Select
              aria-label={t('bcIntegration:filters.status')}
              value={(state.filters?.status as string | undefined) ?? ''}
              placeholder={t('bcIntegration:filters.status')}
              options={[
                { value: '', label: t('bcIntegration:filters.all') },
                ...STATUSES.map((value) => ({ value, label: t(`bcIntegration:statuses.${value}`) })),
              ]}
              onChange={(event) => {
                setFilter('status', event.target.value || undefined);
              }}
            />
            <Select
              aria-label={t('bcIntegration:filters.kind')}
              value={(state.filters?.kind as string | undefined) ?? ''}
              placeholder={t('bcIntegration:filters.kind')}
              options={[
                { value: '', label: t('bcIntegration:filters.all') },
                ...ERP_OUTBOX_KINDS.map((value) => ({ value, label: value })),
              ]}
              onChange={(event) => {
                setFilter('kind', event.target.value || undefined);
              }}
            />
          </>
        }
      />

      <Modal
        open={selected !== null}
        onClose={() => {
          setSelected(null);
        }}
        title={t('bcIntegration:outbox.detailTitle')}
        subtitle={selected?.kind}
        width={720}
      >
        {selected ? (
          <div className="flex flex-col gap-3">
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <Field label={t('bcIntegration:outbox.status')}>
                <OutboxStatusBadge status={selected.status} />
              </Field>
              <Field label={t('bcIntegration:outbox.attempts')}>{selected.attempts}</Field>
              <Field label={t('bcIntegration:outbox.created')}>{formatDateTime(selected.createdAt)}</Field>
              <Field label={t('bcIntegration:outbox.sentAt')}>
                {selected.sentAt ? formatDateTime(selected.sentAt) : '—'}
              </Field>
              <Field label={t('bcIntegration:outbox.idempotencyKey')}>
                <span className="font-mono text-xs">{selected.idempotencyKey ?? '—'}</span>
              </Field>
            </dl>
            {selected.lastError ? (
              <p className="rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-700">{selected.lastError}</p>
            ) : null}
            <PayloadBlock value={selected.payload} />
          </div>
        ) : null}
      </Modal>
    </>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div>
      <dt className="font-mono text-[0.6875rem] uppercase tracking-[0.14em] text-ink-muted">{label}</dt>
      <dd className="mt-0.5 text-ink">{children}</dd>
    </div>
  );
}
